import React, { createContext, useContext, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { fmtEur } from '../../lib/constants';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  ShoppingCart, Plus, Minus, Trash2, Check, ChefHat, Clock, ArrowLeft, X,
  Utensils, ShoppingBag, Bike, Copy, MapPin, BadgeCheck, Banknote, CreditCard,
} from 'lucide-react';
import { TimeSlotPicker, useTimeSlots } from './TimeSlotPicker';
import { AddressMapPicker } from './AddressMapPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RemoteOrderContext = createContext(null);
let LINE_SEQ = 0;

export const RemoteOrderProvider = ({ remote, offsite, restaurantId, tableId, tableName, forcedType, children }) => {
  const [cart, setCart] = useState([]);
  const [customizing, setCustomizing] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  const addToCart = (line) => setCart((c) => [...c, { ...line, lineId: `l${++LINE_SEQ}` }]);
  const removeLine = (lineId) => setCart((c) => c.filter((l) => l.lineId !== lineId));
  const setQty = (lineId, qty) => setCart((c) => c.map((l) => (l.lineId === lineId ? { ...l, qty: Math.max(1, qty) } : l)));
  const clear = () => setCart([]);

  const count = cart.reduce((n, l) => n + l.qty, 0);
  const total = cart.reduce((s, l) => s + (l.unitPrice + l.extras.reduce((e, x) => e + x.price, 0)) * l.qty, 0);

  const remoteEnabled = !!remote?.enabled;
  const takeaway = !!offsite?.takeaway_enabled;
  const delivery = !!offsite?.delivery_enabled;
  const enabled = remoteEnabled || takeaway || delivery;

  const value = {
    enabled, remote: remote || {}, offsite: offsite || {}, remoteEnabled, takeaway, delivery,
    restaurantId, tableId, tableName, forcedType: forcedType || null,
    cart, addToCart, removeLine, setQty, clear, count, total,
    customizing, setCustomizing, cartOpen, setCartOpen,
  };
  return <RemoteOrderContext.Provider value={value}>{children}</RemoteOrderContext.Provider>;
};

export const useRemoteOrder = () => useContext(RemoteOrderContext);

export const AddToCartButton = ({ product, variant = 'default' }) => {
  const ctx = useRemoteOrder();
  const { t } = useTranslation();
  if (!ctx || !ctx.enabled) return null;
  if (variant === 'icon') {
    return (
      <button
        onClick={() => ctx.setCustomizing(product)}
        data-testid={`menu-add-${product.id}`}
        aria-label={t('publicMenu.order.addToCart')}
        className="absolute -bottom-2.5 -right-2.5 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-[3px] ring-card transition-transform duration-150 hover:scale-110 active:scale-95"
      >
        <Plus className="h-5 w-5" strokeWidth={2.6} />
      </button>
    );
  }
  return (
    <Button size="sm" className="mt-2.5 rounded-full h-9 px-4 shadow-sm-token transition-transform duration-150 active:scale-95" data-testid={`menu-add-${product.id}`} onClick={() => ctx.setCustomizing(product)}>
      <Plus className="h-4 w-4 mr-1" strokeWidth={2.6} /> {t('publicMenu.order.addToCart')}
    </Button>
  );
};

const CustomizeDialog = () => {
  const ctx = useRemoteOrder();
  const { t } = useTranslation();
  const product = ctx?.customizing;
  const [variant, setVariant] = useState(null);
  const [extras, setExtras] = useState([]);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');

  React.useEffect(() => {
    if (product) {
      setVariant(product.variants?.length ? product.variants[0].name : null);
      setExtras([]); setQty(1); setNote('');
    }
  }, [product]);

  if (!product) return null;
  const variantDelta = product.variants?.find((v) => v.name === variant)?.price_delta || 0;
  const unitPrice = Number(product.price || 0) + Number(variantDelta);
  const extrasTotal = extras.reduce((s, x) => s + x.price, 0);
  const lineTotal = (unitPrice + extrasTotal) * qty;

  const toggleExtra = (ex) => {
    setExtras((cur) => cur.some((e) => e.name === ex.name)
      ? cur.filter((e) => e.name !== ex.name)
      : [...cur, { name: ex.name, price: Number(ex.price || 0) }]);
  };
  const confirm = () => {
    ctx.addToCart({ product, qty, variant, extras, notes: note.trim(), unitPrice });
    ctx.setCustomizing(null);
    toast.success(t('publicMenu.order.addedToCart', { defaultValue: 'Aggiunto al carrello' }));
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && ctx.setCustomizing(null)}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-auto" data-testid="menu-customize-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle className="font-display">{product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {product.variants?.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('publicMenu.order.variant')}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button key={v.name} onClick={() => setVariant(v.name)} data-testid={`menu-variant-${v.name}`}
                    className={`rounded-xl border px-3 h-9 text-sm font-medium transition-colors ${variant === v.name ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                    {v.name}{Number(v.price_delta) ? ` +${fmtEur(v.price_delta)}` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.extras?.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('publicMenu.order.extras')}</Label>
              <div className="mt-2 space-y-1.5">
                {product.extras.map((ex) => {
                  const on = extras.some((e) => e.name === ex.name);
                  return (
                    <button key={ex.name} onClick={() => toggleExtra(ex)} data-testid={`menu-extra-${ex.name}`}
                      className={`w-full flex items-center justify-between rounded-xl border px-3 h-10 text-sm transition-colors ${on ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}>
                      <span className="flex items-center gap-2">
                        <span className={`h-4 w-4 rounded border flex items-center justify-center ${on ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>{on && <Check className="h-3 w-3" />}</span>
                        {ex.name}
                      </span>
                      {Number(ex.price) > 0 && <span className="text-muted-foreground">+{fmtEur(ex.price)}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('publicMenu.order.note')}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5" placeholder={t('publicMenu.order.notePlaceholder')} data-testid="menu-item-note" />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('publicMenu.order.qty')}</Label>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="menu-qty-minus"><Minus className="h-4 w-4" /></Button>
              <span className="w-6 text-center font-display font-bold" data-testid="menu-qty-value">{qty}</span>
              <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => setQty((q) => q + 1)} data-testid="menu-qty-plus"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button className="w-full rounded-xl h-11" onClick={confirm} data-testid="menu-add-confirm">
            {t('publicMenu.order.add')} · {fmtEur(lineTotal)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const lineExtrasLabel = (l) => l.extras.map((e) => e.name).join(', ');

const ORDER_TYPE_META = {
  tavolo: { icon: Utensils, testid: 'order-type-al-tavolo' },
  asporto: { icon: ShoppingBag, testid: 'order-type-asporto' },
  domicilio: { icon: Bike, testid: 'order-type-domicilio' },
};

const CartSheet = () => {
  const ctx = useRemoteOrder();
  const { t } = useTranslation();
  const cfg = ctx.offsite;

  const modes = useMemo(() => {
    const m = [];
    if (ctx.remoteEnabled) m.push('tavolo');
    if (ctx.takeaway) m.push('asporto');
    if (ctx.delivery) m.push('domicilio');
    return m;
  }, [ctx.remoteEnabled, ctx.takeaway, ctx.delivery]);

  const [step, setStep] = useState('cart');     // cart | type | checkout | done
  const [orderType, setOrderType] = useState(ctx.forcedType || modes[0] || 'tavolo');
  // shared
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [timeSlot, setTimeSlot] = useState('asap');
  const [payment, setPayment] = useState('cash');
  const [cashChange, setCashChange] = useState('');
  const [delivery, setDelivery] = useState(null); // { address, geo, within }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);     // { status, code, token } or tavolo string

  const open = ctx?.cartOpen;
  const reset = () => { setStep('cart'); setError(''); };
  const close = () => { ctx.setCartOpen(false); setTimeout(reset, 200); };

  const fee = orderType === 'domicilio' ? Number(cfg?.delivery_fee || 0) : 0;
  const grandTotal = ctx.total + fee;

  const goNext = () => {
    setError('');
    if (ctx.forcedType) { setOrderType(ctx.forcedType); setStep('checkout'); }
    else if (modes.length > 1) setStep('type');
    else { setOrderType(modes[0] || 'tavolo'); setStep('checkout'); }
  };

  const submit = async () => {
    setError('');
    try {
      if (orderType === 'tavolo') {
        if (!ctx.tableId && !tableNumber.trim()) { setError(t('publicMenu.order.tableNumberRequired')); return; }
        setSubmitting(true);
        const { data } = await axios.post(`${BACKEND_URL}/api/public/orders/${ctx.restaurantId}`, {
          table_id: ctx.tableId || null,
          table_number: ctx.tableId ? null : tableNumber.trim(),
          customer_name: name.trim(),
          note: note.trim(),
          items: ctx.cart.map((l) => ({ product_id: l.product.id, qty: l.qty, variant: l.variant || null, extras: l.extras, notes: l.notes || '' })),
        });
        setResult({ mode: 'tavolo', status: data.status });
        setStep('done'); ctx.clear();
        return;
      }
      // asporto / domicilio
      if (!name.trim()) { setError(t('offsite.nameRequired')); return; }
      if (!phone.trim()) { setError(t('offsite.phoneRequired')); return; }
      if (orderType === 'domicilio') {
        if (!delivery?.geo) { setError(t('offsite.addressRequired')); return; }
        if (delivery?.within === false) { setError(t('offsite.outOfRadius')); return; }
      }
      setSubmitting(true);
      const { data } = await axios.post(`${BACKEND_URL}/api/offsite/submit/${ctx.restaurantId}`, {
        type: orderType,
        items: ctx.cart.map((l) => ({ product_id: l.product.id, qty: l.qty, variant: l.variant || null, extras: l.extras, notes: l.notes || '' })),
        customer_name: name.trim(),
        phone: phone.trim(),
        address: orderType === 'domicilio' ? (delivery?.address || '') : '',
        geo: orderType === 'domicilio' ? delivery?.geo : null,
        notes: note.trim(),
        time_slot: timeSlot,
        payment_method: payment,
        cash_change: payment === 'cash' && cashChange ? Number(cashChange) : null,
      });
      setResult({ mode: orderType, status: data.status, code: data.confirmation_code, token: data.tracking_token });
      setStep('done'); ctx.clear();
    } catch (e) {
      setError(e?.response?.data?.detail || t('publicMenu.order.error'));
    } finally { setSubmitting(false); }
  };

  const startNew = () => { setResult(null); setStep('cart'); setName(''); setPhone(''); setNote(''); setDelivery(null); setTimeSlot('asap'); setPayment('cash'); setCashChange(''); ctx.setCartOpen(false); };
  const copyCode = () => { try { navigator.clipboard.writeText(result.code); toast.success(t('offsite.codeCopied')); } catch (e) { /* */ } };

  const slotState = useTimeSlots(cfg);
  const checkoutValid = (() => {
    if (orderType === 'tavolo') return ctx.tableId || tableNumber.trim();
    if (!name.trim() || !phone.trim()) return false;
    if (orderType === 'domicilio') return !!delivery?.geo && delivery?.within !== false;
    return true;
  })();

  const headerTitle = step === 'done' ? t('offsite.confirmTitle')
    : step === 'type' ? t('offsite.chooseType')
    : step === 'checkout' ? t('publicMenu.order.checkoutTitle')
    : t('publicMenu.order.yourOrder');

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? ctx.setCartOpen(true) : close())}>
      <SheetContent side="bottom" hideClose className="rounded-t-3xl max-h-[94vh] p-0 flex flex-col" data-testid="menu-cart-sheet">
        <SheetHeader className="px-5 pt-5 pb-3 flex-row items-center justify-between space-y-0 border-b border-border">
          <SheetTitle className="font-display flex items-center gap-2">
            {(step === 'checkout' || step === 'type') && (
              <button onClick={() => setStep(step === 'checkout' && modes.length > 1 && !ctx.forcedType ? 'type' : 'cart')} className="mr-1" data-testid="menu-checkout-back"><ArrowLeft className="h-5 w-5" /></button>
            )}
            {headerTitle}
          </SheetTitle>
          <button onClick={close} className="text-muted-foreground" data-testid="menu-cart-close"><X className="h-5 w-5" /></button>
        </SheetHeader>

        {step === 'done' ? (
          <div className="flex-1 overflow-auto px-6 py-8 text-center flex flex-col items-center">
            {result?.mode === 'tavolo' ? (
              <>
                <div className={`h-16 w-16 rounded-full flex items-center justify-center ${result.status === 'sent' ? 'bg-pos-free/15 text-pos-free' : 'bg-accent/15 text-accent'}`}>
                  {result.status === 'sent' ? <ChefHat className="h-8 w-8" /> : <Clock className="h-8 w-8" />}
                </div>
                <div className="mt-4 font-display text-xl font-extrabold" data-testid="menu-order-success">
                  {result.status === 'sent' ? t('publicMenu.order.successSent') : t('publicMenu.order.successPending')}
                </div>
              </>
            ) : (
              <>
                <div className="h-14 w-14 rounded-full flex items-center justify-center bg-pos-free/15 text-pos-free"><BadgeCheck className="h-7 w-7" /></div>
                <div className="mt-3 font-display text-xl font-extrabold" data-testid="menu-order-success">{t('offsite.received')}</div>
                <p className="text-sm text-muted-foreground mt-1">{t('offsite.codeHint')}</p>
                <div className="mt-4 w-full max-w-xs">
                  <div className="font-mono tabular tracking-widest text-3xl sm:text-4xl bg-secondary rounded-[var(--radius)] px-4 py-5 border" data-testid="order-confirmation-code">{result.code}</div>
                  <Button variant="secondary" size="sm" className="mt-3 rounded-xl" onClick={copyCode} data-testid="order-confirmation-copy-button">
                    <Copy className="h-4 w-4 mr-1.5" /> {t('offsite.copyCode')}
                  </Button>
                </div>
                <a href={`/track/${result.token}`} target="_blank" rel="noreferrer" className="mt-5 w-full max-w-xs" data-testid="order-tracking-link">
                  <Button className="w-full rounded-xl h-11"><MapPin className="h-4 w-4 mr-1.5" /> {t('offsite.trackOrder')}</Button>
                </a>
              </>
            )}
            <Button className="mt-6 rounded-xl" variant="outline" onClick={startNew} data-testid="order-confirmation-done-button">{t('publicMenu.order.newOrder')}</Button>
          </div>
        ) : step === 'type' ? (
          <div className="flex-1 overflow-auto px-5 py-4">
            <RadioGroup value={orderType} onValueChange={setOrderType} className="space-y-2" data-testid="order-type-selector">
              {modes.map((m) => {
                const Meta = ORDER_TYPE_META[m]; const Icon = Meta.icon; const on = orderType === m;
                return (
                  <label key={m} data-testid={Meta.testid}
                    className={`flex items-center gap-3 rounded-[var(--radius)] border bg-card px-4 py-3.5 cursor-pointer transition-colors ${on ? 'border-primary ring-2 ring-ring/30' : 'hover:bg-muted'}`}>
                    <RadioGroupItem value={m} className="sr-only" />
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${on ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}><Icon className="h-5 w-5" /></div>
                    <div className="flex-1">
                      <div className="font-display font-bold text-sm">{t(`offsite.type.${m}`)}</div>
                      <div className="text-xs text-muted-foreground">{t(`offsite.typeHint.${m}`)}</div>
                    </div>
                    {on && <Check className="h-5 w-5 text-primary" />}
                  </label>
                );
              })}
            </RadioGroup>
            <Button className="w-full rounded-xl h-12 mt-4" onClick={() => setStep('checkout')} data-testid="order-type-continue-button">{t('common.continue', { defaultValue: 'Continua' })}</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto px-5 py-4">
              {ctx.cart.length === 0 ? (
                <div className="py-14 text-center text-muted-foreground" data-testid="menu-cart-empty">{t('publicMenu.order.emptyCart')}</div>
              ) : step === 'cart' ? (
                <div className="space-y-3" data-testid="menu-cart-items">
                  {ctx.cart.map((l) => (
                    <div key={l.lineId} className="flex gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`menu-cart-line-${l.lineId}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold leading-tight">{l.product.name}{l.variant ? ` · ${l.variant}` : ''}</div>
                        {l.extras.length > 0 && <div className="text-xs text-muted-foreground mt-0.5">+ {lineExtrasLabel(l)}</div>}
                        {l.notes && <div className="text-xs text-muted-foreground mt-0.5 italic">“{l.notes}”</div>}
                        <div className="mt-2 flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-7 w-7 rounded-full" onClick={() => ctx.setQty(l.lineId, l.qty - 1)} data-testid={`menu-line-minus-${l.lineId}`}><Minus className="h-3.5 w-3.5" /></Button>
                          <span className="w-5 text-center text-sm font-bold">{l.qty}</span>
                          <Button variant="outline" size="icon" className="h-7 w-7 rounded-full" onClick={() => ctx.setQty(l.lineId, l.qty + 1)} data-testid={`menu-line-plus-${l.lineId}`}><Plus className="h-3.5 w-3.5" /></Button>
                          <button onClick={() => ctx.removeLine(l.lineId)} className="ml-auto text-destructive" data-testid={`menu-line-remove-${l.lineId}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                      <div className="font-display font-black text-primary tabular shrink-0">{fmtEur((l.unitPrice + l.extras.reduce((s, x) => s + x.price, 0)) * l.qty)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                /* CHECKOUT */
                <div className="space-y-5" data-testid={orderType === 'domicilio' ? 'delivery-checkout-sheet' : orderType === 'asporto' ? 'takeaway-checkout-sheet' : 'menu-checkout-form'}>
                  {/* table (tavolo) */}
                  {orderType === 'tavolo' && (
                    ctx.tableId ? (
                      <div className="rounded-xl bg-primary/10 text-primary px-4 py-3 text-sm font-semibold" data-testid="menu-table-banner">
                        {t('publicMenu.order.atTable')} <span className="font-black">{ctx.tableName || ''}</span>
                      </div>
                    ) : (
                      <div>
                        <Label>{t('publicMenu.order.tableNumber')}</Label>
                        <Input value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} className="mt-1.5" placeholder={t('publicMenu.order.tableNumberPlaceholder')} data-testid="menu-table-input" />
                      </div>
                    )
                  )}

                  {/* address + map (domicilio) */}
                  {orderType === 'domicilio' && (
                    <AddressMapPicker restaurantId={ctx.restaurantId} config={cfg} onResolve={setDelivery} />
                  )}

                  {/* time slots (asporto/domicilio) */}
                  {orderType !== 'tavolo' && (
                    <div>
                      <Label className="mb-1.5 block">{orderType === 'asporto' ? t('offsite.pickupTime') : t('offsite.deliveryTime')}</Label>
                      <TimeSlotPicker cfg={cfg} value={timeSlot} onChange={setTimeSlot}
                        testId={orderType === 'asporto' ? 'takeaway-time-slot-group' : 'delivery-time-slot-group'} />
                    </div>
                  )}

                  {/* contact */}
                  <div className="space-y-3">
                    <div>
                      <Label>{t('publicMenu.order.customerName')}</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5"
                        placeholder={t('publicMenu.order.customerNamePlaceholder')}
                        data-testid={orderType === 'asporto' ? 'takeaway-name-input' : 'menu-customer-name'} />
                    </div>
                    {orderType !== 'tavolo' && (
                      <div>
                        <Label>{t('offsite.phone')}</Label>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" inputMode="tel"
                          placeholder={t('offsite.phonePlaceholder')}
                          data-testid={orderType === 'asporto' ? 'takeaway-phone-input' : 'delivery-phone-input'} />
                      </div>
                    )}
                    <div>
                      <Label>{orderType === 'domicilio' ? t('offsite.deliveryNotes') : t('publicMenu.order.generalNote')}</Label>
                      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5"
                        placeholder={orderType === 'domicilio' ? t('offsite.deliveryNotesPlaceholder') : t('publicMenu.order.generalNotePlaceholder')}
                        data-testid={orderType === 'domicilio' ? 'delivery-notes-textarea' : orderType === 'asporto' ? 'takeaway-notes-textarea' : 'menu-general-note'} />
                    </div>
                  </div>

                  {/* payment (asporto/domicilio) */}
                  {orderType !== 'tavolo' && (
                    <div>
                      <Label className="mb-2 block">{t('offsite.payment')}</Label>
                      <RadioGroup value={payment} onValueChange={setPayment} className="grid grid-cols-2 gap-2" data-testid="delivery-payment-method">
                        {[{ k: 'cash', icon: Banknote, label: t('offsite.cash') }, { k: 'card', icon: CreditCard, label: t('offsite.card') }].map(({ k, icon: Icon, label }) => (
                          <label key={k} data-testid={`payment-${k}`}
                            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 cursor-pointer text-sm transition-colors ${payment === k ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted'}`}>
                            <RadioGroupItem value={k} className="sr-only" />
                            <Icon className="h-4 w-4" /> {label}
                          </label>
                        ))}
                      </RadioGroup>
                      {payment === 'cash' && (
                        <div className="mt-2">
                          <Label className="text-xs text-muted-foreground">{t('offsite.cashChange')}</Label>
                          <Input value={cashChange} onChange={(e) => setCashChange(e.target.value)} inputMode="decimal" type="number"
                            className="mt-1" placeholder="50" data-testid="delivery-cash-change-input" />
                        </div>
                      )}
                    </div>
                  )}

                  {error && <div className="text-sm text-destructive font-medium" data-testid="menu-checkout-error">{error}</div>}
                </div>
              )}
            </div>

            {ctx.cart.length > 0 && (
              <div className="border-t border-border p-4 space-y-3">
                {step === 'checkout' && fee > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{t('offsite.subtotal')}</span><span className="tabular">{fmtEur(ctx.total)}</span></div>
                    <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{t('offsite.deliveryFee')}</span><span className="tabular">{fmtEur(fee)}</span></div>
                    <Separator />
                  </>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('publicMenu.order.total')}</span>
                  <span className="font-display text-xl font-black text-primary tabular" data-testid="menu-cart-total">{fmtEur(step === 'checkout' ? grandTotal : ctx.total)}</span>
                </div>
                {step === 'cart' ? (
                  <Button className="w-full rounded-xl h-12" onClick={goNext} data-testid="menu-goto-checkout">{t('publicMenu.order.sendOrder')}</Button>
                ) : (
                  <Button className="w-full rounded-xl h-12" onClick={submit} disabled={submitting || !checkoutValid}
                    data-testid={orderType === 'domicilio' ? 'delivery-submit-order-button' : orderType === 'asporto' ? 'takeaway-submit-order-button' : 'menu-submit-order'}>
                    {submitting ? t('publicMenu.order.submitting') : t('publicMenu.order.submit')}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const RemoteCart = () => {
  const ctx = useRemoteOrder();
  const { t } = useTranslation();
  if (!ctx || !ctx.enabled) return null;
  return (
    <>
      {ctx.count > 0 && !ctx.cartOpen && (
        <button onClick={() => ctx.setCartOpen(true)} data-testid="menu-cart-fab"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-3 rounded-full bg-primary px-5 h-13 py-3 text-primary-foreground shadow-lg-token font-display font-bold transition-transform active:scale-95">
          <span className="relative">
            <ShoppingCart className="h-5 w-5" />
            <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] flex items-center justify-center" data-testid="menu-cart-count">{ctx.count}</span>
          </span>
          <span>{t('publicMenu.order.cart')}</span>
          <span className="tabular">{fmtEur(ctx.total)}</span>
        </button>
      )}
      <CustomizeDialog />
      <CartSheet />
    </>
  );
};
